import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { QueryUnit } from '@app/models/queries/queries.model';

// eslint-disable-next-line no-shadow
export enum UnitType {
    GALLONS = 'gallons',
    CUBIC_FEET = 'cubic_feet',
    CUBIC_METERS = 'cubic_meters',
    LITERS = 'liters',
}

export interface Unit {
    type: UnitType;
    name: string;
    short: string;
    query: QueryUnit;
    mult: number;
    convert: (amt: number, from: UnitType) => number;
}

@Injectable({
    providedIn: 'root',
})
export class UnitService {
    private units: Record<string, Unit> = {
        gallons: {
            type: UnitType.GALLONS,
            name: 'Gallons',
            short: 'gal',
            query: 'GALLONS',
            mult: 1.0,
            convert: this.unitConvertFn(UnitType.GALLONS),
        },
        cubic_feet: {
            type: UnitType.CUBIC_FEET,
            name: 'Cubic Feet',
            short: 'cu ft',
            query: 'CUBIC_FEET',
            mult: 0.133681,
            convert: this.unitConvertFn(UnitType.CUBIC_FEET),
        },
        cubic_meters: {
            type: UnitType.CUBIC_METERS,
            name: 'Cubic Meters',
            short: 'cm',
            query: 'CUBIC_METERS',
            mult: 0.00378541,
            convert: this.unitConvertFn(UnitType.CUBIC_METERS),
        },
        liters: {
            type: UnitType.LITERS,
            name: 'Liters',
            short: 'L',
            query: 'LITERS',
            mult: 3.78541,
            convert: this.unitConvertFn(UnitType.LITERS),
        },
    };

    private current: BehaviorSubject<UnitType> = new BehaviorSubject(UnitType.GALLONS);
    private localStorageName = 'unit';

    constructor() {
        const stored = localStorage.getItem(this.localStorageName) as UnitType;
        if (this.units[stored]) {
            this.current.next(stored);
        } else {
            localStorage.setItem(this.localStorageName, this.current.value.toString());
        }
    }

    get onChange() {
        return this.current.asObservable();
    }

    convertUnits(amount: number, from: UnitType, to: UnitType) {
        return (amount / this.units[from].mult) * this.units[to].mult;
    }

    convertFromCurrent(amount: number, unit: UnitType) {
        return this.convertUnits(amount, unit, this.current.value);
    }

    convertToCurrent(amount: number, unit: UnitType) {
        return this.convertUnits(amount, unit, this.current.value);
    }

    convertFromGallonsToCurrent(amount: number) {
        return this.convertUnits(amount, UnitType.GALLONS, this.current.value);
    }

    convertFromCurrentToGallons(amount: number) {
        return this.convertUnits(amount, this.current.value, UnitType.GALLONS);
    }

    get(): Unit {
        return this.units[this.current.value];
    }

    set(unit: UnitType) {
        this.current.next(unit);
        localStorage.setItem(this.localStorageName, unit.toString().toLowerCase());
    }

    getAll(): Unit[] {
        return Object.values(this.units);
    }

    private unitConvertFn(unit: UnitType) {
        return ((amt: number, from: UnitType) => this.convertUnits(amt, from, unit)).bind(this);
    }
}
